/**
 * Página de Modificadores Globales — ListView + useListViews + useStandardListFilters.
 * Misma estructura y diseño que Categorías/Etiquetas; solo cambian datos y config.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BlockStack,
  Banner,
  ChoiceList,
  Modal,
  Text,
  TextField,
  IndexFiltersMode,
  useSetIndexFiltersMode,
} from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { deleteModifiersByIdentity } from '@/lib/modifiersApi';
import { fetchAllProductRows, fetchAllByProductIds } from '@/lib/productsApi';
import { useAuth, usePagination } from '@smarty/shared';
import { useAdminToast } from '@/contexts/AdminToastContext';
import { routeHelpers, storePath, slugifyModifierName } from '@/config/routes.config';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { ListView, buildListViewPagination } from '@/components/admin/ListView';
import { getModifiersListConfig } from '@/config/listViews';
import { getStatsPageCache, setStatsPageCache, listViewCacheKey } from '@/lib/statsPageCache';
import type { ModifierListItem } from '@/config/listViews';
import { filterBySearchName } from '@/lib/listViewFilters';
import { useListViews } from '@/hooks/useListViews';
import { useCanManage } from '@/hooks/useCanManage';
import { useStandardListFilters } from '@/hooks/useStandardListFilters';
import { exportListToExcel, getExportFilename } from '@/lib/exportToExcel';
import { ProductIcon } from '@/lib/polaris-icons';

const LIST_ID = 'modifiers';

const MODIFIERS_SYSTEM_VIEWS = [
  {
    id: 'todos',
    label: 'Todos',
    isSystem: true,
    query: '',
    filterValues: { priceType: [] as string[] },
  },
];

const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'price_desc', label: 'Mayor ajuste primero' },
  { value: 'price_asc', label: 'Menor ajuste primero' },
] as const;

const SORT_OPTIONS_POLARIS = SORT_OPTIONS.map((o) => ({
  label: o.label,
  value: o.value.replace('_', ' ') as `${string} ${'asc' | 'desc'}`,
  directionLabel: o.label,
}));

const PRICE_TYPE_FILTER_OPTIONS = [
  { label: 'Positivo (+)', value: 'positive' },
  { label: 'Negativo (-)', value: 'negative' },
  { label: 'Sin ajuste (0)', value: 'zero' },
] as const;

type ModifierRow = { id: string; slug: string | null; name: string; price_adjustment: number; created_at: string };

function filterModifiersByPriceType(items: ModifierListItem[], priceType: string[]): ModifierListItem[] {
  if (priceType.length === 0) return items;
  return items.filter((m) => {
    const adj = Number(m.price_adjustment);
    return (
      (priceType.includes('positive') && adj > 0) ||
      (priceType.includes('negative') && adj < 0) ||
      (priceType.includes('zero') && adj === 0)
    );
  });
}

function sortModifiers(items: ModifierListItem[], sortOption: string): ModifierListItem[] {
  const list = [...items];
  const [key, dir] = sortOption.includes('_') ? sortOption.split('_') : [sortOption, 'asc'];
  if (key === 'name') {
    list.sort((a, b) => {
      const c = a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      return dir === 'desc' ? -c : c;
    });
  } else if (key === 'price') {
    list.sort((a, b) => {
      const va = Number(a.price_adjustment);
      const vb = Number(b.price_adjustment);
      return dir === 'desc' ? vb - va : va - vb;
    });
  }
  return list;
}

export default function ModifiersPage() {
  const { storeSlug } = useParams<{ storeSlug?: string }>();
  const navigate = useNavigate();
  const { tenantId } = useAuth();
  const canManage = useCanManage();
  const toast = useAdminToast();

  const [modifiers, setModifiers] = useState<ModifierListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modifierToDelete, setModifierToDelete] = useState<{ id: string; name: string; slug: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [newModifierName, setNewModifierName] = useState('');
  const [newModifierPrice, setNewModifierPrice] = useState<string>('0');
  const [addSaving, setAddSaving] = useState(false);

  const [searchValue, setSearchValue] = useState('');
  const [filterPriceType, setFilterPriceType] = useState<string[]>([]);
  const [sortOption, setSortOption] = useState<string>(SORT_OPTIONS[0].value);
  const { mode, setMode } = useSetIndexFiltersMode(IndexFiltersMode.Default);

  const filtersRecord = useMemo(
    () => ({ priceType: filterPriceType }),
    [filterPriceType]
  );
  const setFiltersFromView = useCallback((f: Record<string, string[]>) => {
    setFilterPriceType(f.priceType ?? []);
  }, []);

  const views = useListViews({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    systemViews: MODIFIERS_SYSTEM_VIEWS,
    searchValue,
    filters: filtersRecord,
    setSearchValue,
    setFilters: setFiltersFromView,
    setMode,
    mode,
  });
  const { activeView, handleSwitchView } = views;

  const toUrl = useCallback(
    (path: string) => navigate(storeSlug ? storePath(storeSlug, path) : path),
    [navigate, storeSlug]
  );
  const config = useMemo(
    () => getModifiersListConfig(toUrl, { onAddModifier: () => setAddModalOpen(true) }),
    [toUrl]
  );

  const appliedFilters = useMemo(() => {
    const items: { key: string; label: string; onRemove: () => void }[] = [];
    if (filterPriceType.length > 0) {
      const labels = filterPriceType
        .map((v) => PRICE_TYPE_FILTER_OPTIONS.find((o) => o.value === v)?.label ?? v)
        .join(', ');
      items.push({
        key: 'priceType',
        label: `Ajuste de precio: ${labels}`,
        onRemove: () => setFilterPriceType([]),
      });
    }
    return items;
  }, [filterPriceType]);

  const indexFiltersFilters = useMemo(
    () => [
      {
        key: 'priceType',
        label: 'Ajuste de precio',
        shortcut: true,
        pinned: true,
        filter: (
          <ChoiceList
            title="Ajuste de precio"
            titleHidden
            choices={PRICE_TYPE_FILTER_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            selected={filterPriceType}
            onChange={setFilterPriceType}
            allowMultiple
          />
        ),
      },
    ],
    [filterPriceType]
  );

  const closeAddModal = useCallback(() => {
    setAddModalOpen(false);
    setNewModifierName('');
    setNewModifierPrice('0');
  }, []);

  const clearFilters = useCallback(() => handleSwitchView(0), [handleSwitchView]);

  const {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode,
    editingColumnPrefs,
    onColumnOrderChange,
    onToggleColumnVisibility,
  } = useStandardListFilters({
    listId: LIST_ID,
    tenantId: tenantId ?? undefined,
    config,
    views,
    searchValue,
    setSearchValue,
    queryPlaceholder: activeView ? `Buscando en ${activeView.label}` : 'Buscar modificadores',
    filters: indexFiltersFilters,
    appliedFilters,
    onClearAll: clearFilters,
    sortOptions: SORT_OPTIONS_POLARIS,
    sortSelected: [sortOption.replace('_', ' ')],
    onSort: (v) => setSortOption((v[0] ?? sortOption).replace(' ', '_')),
    mode,
    setMode,
  });

  const loadModifiers = useCallback(async (revalidateOnly = false) => {
    if (!tenantId) return;
    const cacheKey = listViewCacheKey('modifiers', tenantId);
    if (!revalidateOnly) {
      const cached = getStatsPageCache<{ items: ModifierListItem[] }>(cacheKey);
      if (cached?.items !== undefined) {
        setModifiers(cached.items);
        setLoading(false);
        loadModifiers(true).catch(() => {});
        return;
      }
    }
    if (!revalidateOnly) setLoading(true);
    try {
      // Ambas queries se paginan: antes truncaban en 1000 (PostgREST) → con catálogos
      // grandes faltaban modificadores en la lista, en silencio.
      const productIds = (await fetchAllProductRows<{ id: string }>(tenantId, 'id')).map((p) => p.id);

      if (productIds.length === 0) {
        setModifiers([]);
        setLoading(false);
        return;
      }

      const data = await fetchAllByProductIds<ModifierRow>(
        'product_modifiers',
        'id, slug, name, price_adjustment, created_at',
        productIds
      );
      // Preserva el `.order('name')` original: la deduplicación de abajo se queda con la
      // PRIMERA fila de cada nombre, así que el orden importa para qué id/slug conserva.
      data.sort((a, b) => a.name.localeCompare(b.name));

      const unique = new Map<string, ModifierListItem>();
      (data ?? []).forEach((row: ModifierRow) => {
        if (unique.has(row.name)) return;
        unique.set(row.name, {
          id: row.id,
          slug: row.slug ?? '',
          name: row.name,
          price_adjustment: row.price_adjustment,
          tenant_id: tenantId,
          created_at: row.created_at,
          is_active: true,
        });
      });
      const list = Array.from(unique.values());
      setModifiers(list);
      setStatsPageCache(cacheKey, { items: list });
    } catch (error: unknown) {
      toast.error('Error al cargar modificadores', error instanceof Error ? error.message : undefined);
    } finally {
      setLoading(false);
    }
  }, [tenantId, toast]);

  useLayoutEffect(() => {
    if (tenantId) loadModifiers();
  }, [tenantId, loadModifiers]);

  const filteredModifiers = useMemo(() => {
    const bySearch = filterBySearchName(modifiers, searchValue);
    const byPriceType = filterModifiersByPriceType(bySearch, filterPriceType);
    return sortModifiers(byPriceType, sortOption);
  }, [modifiers, searchValue, filterPriceType, sortOption]);

  const pagination = usePagination();
  useEffect(() => {
    pagination.setTotal(filteredModifiers.length);
    if (filteredModifiers.length > 0 && pagination.offset >= filteredModifiers.length) {
      pagination.goToFirstPage();
    }
  }, [filteredModifiers.length, pagination]);

  const listItems = useMemo(
    () => filteredModifiers.slice(pagination.offset, pagination.offset + pagination.pageSize),
    [filteredModifiers, pagination.offset, pagination.pageSize]
  );

  const handleBulkAction = useCallback(
    (actionKey: string, selectedIds: string[]) => {
      if (actionKey === 'export' && selectedIds.length > 0) {
        const items = filteredModifiers.filter((m) => selectedIds.includes(m.id));
        exportListToExcel({
          columns: config.columns,
          items: items.map((i) => ({ ...i })),
          filename: getExportFilename(config.title),
        });
        toast.success('Exportado', `${items.length} modificador(es) exportado(s) a Excel`);
      }
    },
    [config.columns, config.title, filteredModifiers, toast]
  );

  const paginationOption = buildListViewPagination(pagination);

  const closeDeleteModal = useCallback(() => {
    if (!deleting) {
      setDeleteModalOpen(false);
      setModifierToDelete(null);
    }
  }, [deleting]);

  const handleDeleteClick = useCallback((modifier: ModifierListItem) => {
    setModifierToDelete({ id: modifier.id, name: modifier.name, slug: modifier.slug });
    setDeleteModalOpen(true);
  }, []);

  /**
   * Borra un modificador "global" en TODAS sus filas (una por producto), coherente con editar.
   * La semántica vive en `@/lib/modifiersApi`: antes estaba copiada acá y en ModifierEditPage,
   * y se desincronizaron (la de allá quedó truncada en 1000 productos).
   */
  const deleteModifiers = useCallback(
    async (items: { slug: string; name: string }[]) => {
      if (!tenantId) return;
      await deleteModifiersByIdentity(tenantId, items);
    },
    [tenantId]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!modifierToDelete) return;
    setDeleting(true);
    try {
      await deleteModifiers([{ slug: modifierToDelete.slug, name: modifierToDelete.name }]);
      toast.success('Modificador eliminado', modifierToDelete.name);
      setDeleteModalOpen(false);
      setModifierToDelete(null);
      await loadModifiers();
    } catch (error: unknown) {
      toast.error('Error al eliminar modificador', error instanceof Error ? error.message : undefined);
    } finally {
      setDeleting(false);
    }
  }, [modifierToDelete, deleteModifiers, loadModifiers, toast]);

  const handleRowEdit = useCallback(
    (item: ModifierListItem) => toUrl(routeHelpers.modifierEdit(item.slug || item.id)),
    [toUrl]
  );

  // Borrado masivo: la plantilla pone la acción destructiva, el modal y el toast.
  // deleteModifiers borra por identidad (slug/name), así que mapeamos ids → objetos.
  const bulkDeleteModifiers = useCallback(async (ids: string[]) => {
    const selected = modifiers.filter((m) => ids.includes(m.id));
    await deleteModifiers(selected.map((m) => ({ slug: m.slug, name: m.name })));
    await loadModifiers();
    return selected.length;
  }, [modifiers, deleteModifiers, loadModifiers]);

  const handleAddModifier = useCallback(async () => {
    if (!tenantId || !newModifierName.trim()) {
      toast.error('El nombre del modificador es requerido');
      return;
    }
    setAddSaving(true);
    try {
      const { data: sampleProduct } = await supabase
        .from('products')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .limit(1)
        .single();

      if (!sampleProduct) {
        toast.error('Primero debes crear al menos un producto activo');
        setAddSaving(false);
        return;
      }

      const name = newModifierName.trim();
      const priceAdjustment = parseFloat(newModifierPrice) || 0;
      const slug = slugifyModifierName(name);

      const { error } = await supabase.from('product_modifiers').insert({
        product_id: sampleProduct.id,
        name,
        slug,
        price_adjustment: priceAdjustment,
        is_required: false,
      });

      if (error) throw error;
      toast.success('Modificador creado', name);
      closeAddModal();
      await loadModifiers();
    } catch (error: unknown) {
      toast.error('Error al crear modificador', error instanceof Error ? error.message : undefined);
    } finally {
      setAddSaving(false);
    }
  }, [tenantId, newModifierName, newModifierPrice, loadModifiers, toast, closeAddModal]);

  const pageTitleNode = (
    <PageBreadcrumbTitle
      icon={ProductIcon}
      title={config.title}
      onBack={config.onBack}
      backAccessibilityLabel={config.backAccessibilityLabel ?? 'Volver a Productos'}
    />
  );

  return (
    <>
      <ListView<ModifierListItem>
        config={config}
        items={listItems}
        loading={loading}
        pagination={paginationOption}
        loadingMessage="Cargando modificadores…"
        pageTitle={pageTitleNode}
        contentBefore={filterControl}
        contentBeforeInsideCard
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        bulkDelete={{ onDelete: bulkDeleteModifiers, can: canManage }}
        columnOrder={tableColumnOrder}
        hiddenColumnKeys={tableHiddenKeys}
        editColumnsMode={editColumnsMode}
        columnOrderForEdit={editingColumnPrefs?.columnOrder?.length ? editingColumnPrefs.columnOrder : tableColumnOrder}
        onColumnOrderChange={onColumnOrderChange}
        onToggleColumnVisibility={onToggleColumnVisibility}
        onRowEdit={handleRowEdit}
        onRowDelete={handleDeleteClick}
        onBulkAction={handleBulkAction}
      />

      <Modal
        open={addModalOpen}
        onClose={() => !addSaving && closeAddModal()}
        title="Agregar modificador"
        primaryAction={{
          content: addSaving ? 'Creando…' : 'Crear',
          onAction: handleAddModifier,
          disabled: addSaving || !newModifierName.trim(),
          loading: addSaving,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: closeAddModal, disabled: addSaving }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Nombre"
              value={newModifierName}
              onChange={setNewModifierName}
              placeholder="ej: Extra Queso, Sin Azúcar…"
              autoComplete="off"
              requiredIndicator
            />
            <TextField
              label="Ajuste de precio"
              type="number"
              value={newModifierPrice}
              onChange={setNewModifierPrice}
              placeholder="0.00"
              prefix="S/"
              autoComplete="off"
              helpText="Valor positivo para sumar o negativo para restar al precio base."
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        title="Eliminar modificador"
        primaryAction={{
          content: deleting ? 'Eliminando…' : 'Eliminar',
          tone: 'critical',
          onAction: handleDeleteConfirm,
          loading: deleting,
        }}
        secondaryActions={[{ content: 'Cancelar', onAction: closeDeleteModal, disabled: deleting }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Banner tone="critical">
              <BlockStack gap="200">
                <Text as="p">¿Estás seguro de que deseas eliminar el modificador "{modifierToDelete?.name}"?</Text>
                <Text variant="bodySm" tone="subdued" as="p">
                  Esta acción no se puede deshacer. Los productos que usen este modificador perderán esta opción.
                </Text>
              </BlockStack>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>

    </>
  );
}
